import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextareaAutosize,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@material-ui/core";
import { API, graphqlOperation } from "aws-amplify";
import { initializeRecaptcha } from '../../utils/recaptcha';

const Customer = () => {
  const [overlay, setOverlay] = useState(false);
  const [customerID, setCustomerID] = useState("");
  const [customer, setCustomer] = useState({});
  const [product, setProduct] = useState({});
  const [reason, setReason] = useState("");
  const [showProduct, setShowProduct] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showReqRefundDialog, setShowReqRefundDialog] = useState(false);
  const [token, setToken] = useState(null);

  const cancelReasonRef = useRef();

  useEffect(() => {
    let interval;

    const getToken = async () => {
      try {
        const newToken = await initializeRecaptcha('customer_action');
        if (newToken) {
          setToken(newToken);
        }
      } catch (error) {
        console.error('トークン取得エラー:', error);
      }
    };

    // 初回実行
    getToken();

    // 2分ごとに更新
    interval = setInterval(getToken, 2 * 60 * 1000);

    // クリーンアップ
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, []);

  const handleKeydownEnter = async (text) => {
    await fetchCustomer(text);
  };

  const fetchCustomer = async (id) => {
    setOverlay(true);
    const query = `
      query GetCustomer {
        getCustomer(id: "${id}") {
          id
          name
          productID
          chargeID
          created_at
          reqRefund
          refundComment
          answer
          cancelled_at
        }
      }
    `;
    try {
      const response = await API.graphql(graphqlOperation(query, { authMode: "AWS_API_KEY" }));
      const fetchedCustomer = response.data.getCustomer;
      if (!fetchedCustomer) {
        throw new Error("Customer not found");
      }
      setCustomer(fetchedCustomer);
      setReason(fetchedCustomer.refundComment || "");
      setShowAnswer(Boolean(fetchedCustomer.answer));
      await fetchProduct(fetchedCustomer.productID, fetchedCustomer.cancelled_at);
    } catch (error) {
      setShowProduct(false);
      setShowError(true);
    } finally {
      setOverlay(false);
    }
  };

  // 商品取得時のチェックポイント
  const fetchProduct = async (id, cancelled_at) => {
    const query = `
      query GetProduct {
        getProduct(id: "${id}") {
          URL
          description
          form
          id
          price
          note
          title
          stripeID
        }
      }
    `;
    try {
      const response = await API.graphql(graphqlOperation(query));
      const fetchedProduct = response.data.getProduct;
      if (!fetchedProduct) {
        throw new Error("Product not found");
      }
      if (cancelled_at) {
        fetchedProduct.URL = "購入がキャンセルされたため閲覧できません";
      }
      setProduct(fetchedProduct);
      setShowError(false);
      setShowProduct(true);
    } catch (error) {
      setShowProduct(false);
      setShowError(true);
    }
  };

  const handleReqRefund = async () => {
    if (!token) {
      alert('セキュリティチェックを実行中です。しばらくお待ちください。');
      return;
    }
    // テキスト入力内容をエスケープ処理
    const escapedReason = reason
      .replace(/\\/g, '\\\\')  // バックスラッシュをエスケープ
      .replace(/"/g, '\\"')    // ダブルクォートをエスケープ
      .replace(/\n/g, '\\n')   // 改行をエスケープ
      .replace(/\r/g, '\\r')   // キャリッジリターンをエスケープ
      .replace(/\t/g, '\\t');  // タブをエスケープ
  
    setOverlay(true);
    const mutation = `
      mutation ReqRefund($input: UpdateReqRefund!) {
        reqRefund(input: $input) {
          id
          name
          reqRefund
          refundComment
        }
      }
    `;

    const variables = {
      input: {
        name: customer.name ?? '',
        customerID: customer.id,
        refundComment: escapedReason,
        productID: customer.productID,
        token: token
      }
    };
    
    try {
      const response = await API.graphql(graphqlOperation(
        mutation, 
        variables, 
        { authMode: "API_KEY" }
      ));
      
      // レスポンスを確認（データの存在有無で判定）
      if (response.data?.reqRefund) {
        // 成功した場合
        setShowReqRefundDialog(true);
        
        // 顧客データ更新（問い合わせした内容を反映）
        setCustomer(prev => ({
          ...prev,
          refundComment: reason,
          reqRefund: Math.floor(Date.now() / 1000) // 現在時刻を UNIX タイムスタンプで記録
        }));
      } else {
        throw new Error("更新に失敗しました");
      }
    } catch (error) {
      console.error("GraphQLエラー詳細:", error);
      alert("お問い合わせに失敗しました");
    } finally {
      setOverlay(false);
    }
  };
  
  return (
    <Box mx={6}>
      {overlay && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      )}
      {!overlay && (
        <>
          <Typography variant="h4" align="center" gutterBottom>
            商品の再閲覧
          </Typography>
          <Box textAlign="center" mb={2}>
            <Typography>購入者IDを入力してください</Typography>
          </Box>
          <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
            <TextField
              label="購入者ID"
              value={customerID}
              onChange={(e) => setCustomerID(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleKeydownEnter(customerID)}
              fullWidth
            />
            <Button onClick={() => handleKeydownEnter(customerID)}>検索</Button>
          </Box>

          {showProduct && (
            <>
              <Typography variant="h5" align="center" gutterBottom>
                購入した商品が見つかりました。
              </Typography>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>商品名</TableCell>
                    <TableCell>{product.title}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>価格</TableCell>
                    <TableCell>{product.price.toLocaleString()} 円</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>商品URL</TableCell>
                    <TableCell>{product.URL}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>購入日時</TableCell>
                    <TableCell>{new Date(customer.created_at * 1000).toLocaleString()}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              {showAnswer && (
                <Box mt={2}>
                  <Typography align="center">出品者より回答が届いています。</Typography>
                  <Typography align="center">{customer.answer}</Typography>
                </Box>
              )}
              <Box mt={4} textAlign="center">
                <TextareaAutosize
                  minRows={3}
                  placeholder="お問い合わせ内容"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  style={{ width: "100%", maxWidth: "400px" }}
                />
              </Box>
              <Box textAlign="center" mt={2}>
                <Button variant="contained" color="secondary" onClick={handleReqRefund}>
                  お問い合わせ送信
                </Button>
              </Box>
              <Dialog open={showReqRefundDialog} onClose={() => setShowReqRefundDialog(false)}>
                <DialogTitle>お問い合わせを送信しました</DialogTitle>
                <DialogActions>
                  <Button onClick={() => setShowReqRefundDialog(false)} color="primary">
                    閉じる
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}

          {showError && (
            <Typography align="center" color="error">
              購入した商品が見つかりませんでした。購入者IDをご確認の上、再度ご入力ください。
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default Customer;